import { COLLECTIONS } from "constant";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { throwException } from "utils";
import { db, storage } from "@firebase";
import { IUser } from "types/types";
import {
  getDownloadURL,
  ref,
  uploadBytes,
  uploadBytesResumable,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";

class User {
  // Get user by email
  getUserByid = async (uid: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      try {
        onSnapshot(doc(db, COLLECTIONS.USER, uid), (snap) => {
          if (snap.exists()) {
            resolve({ ...snap.data(), id: snap.id });
          } else {
            reject("User not found");
          }
        });
      } catch (error: any) {
        reject(throwException(error.code || error.message));
      }
    });
  };

  // Create a new user
  createUser = async (uid: string, userData: any): Promise<void> => {
    try {
      const userRef = doc(db, COLLECTIONS.USER, uid);
      await setDoc(userRef, userData, { merge: true });
    } catch (error: any) {
      throw throwException(error.code || error.message);
    }
  };

  // Update an existing user
  updateUser = async (uid: string, updatedData: any): Promise<void> => {
    try {
      const userRef = doc(db, COLLECTIONS.USER, uid);
      await updateDoc(userRef, updatedData);
    } catch (error: any) {
      throw throwException(error.code || error.message);
    }
  };

  // Delete a user
  deleteUser = async (uid: string): Promise<void> => {
    try {
      const userRef = doc(db, COLLECTIONS.USER, uid);
      await deleteDoc(userRef);
    } catch (error: any) {
      throw throwException(error.code || error.message);
    }
  };

  // List all users
  listAllUsers = async (): Promise<any[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.USER));
      const users: any[] = [];
      querySnapshot.forEach((doc) => {
        users.push({ ...doc.data(), id: doc.id });
      });
      return users;
    } catch (error: any) {
      throw throwException(error.code || error.message);
    }
  };

  // Upload profile picture to Firebase Storage and return the download URL
  uploadProfilePicture = async (uid: string, file: File): Promise<string> => {
    try {
      // Correct the spelling of "assets" and use proper template literal syntax
      const newFilename = uuidv4();
      const storageRef = ref(storage, `files/${newFilename}`);

      // Upload the file to Firebase Storage
      await uploadBytes(storageRef, file);

      // Get and return the download URL
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      throw new Error("Failed to upload profile picture.");
    }
  };

  // Update user profile URL after uploading profile picture
  updateProfileUrl = async (uid: string, profileUrl: string): Promise<void> => {
    try {
      const userRef = doc(db, COLLECTIONS.USER, uid);
      await updateDoc(userRef, { profileUrl });
    } catch (error: any) {
      throw throwException(error.code || error.message);
    }
  };
}

const UserService = new User();

export { UserService };
